import { SetupStatus } from "./type/csv-status";
import { CsvStatusAdapter } from "./adapter/csv-status";
import { DeleteGroupReq, EditGroupReq, AddGroupReq, Group } from "./type/group";
import { DeleteIdCodeReq, EditIdCodeReq, IdCode } from "./type/idcode";
import {
  UploadFileReq,
  StoreFileReq,
  StartUploadReq,
  PresignedUpload,
  IngestStatus,
  TimeGrainConfig,
} from "./type/store";
import { gzipBlob } from "../../lib/gzip";
import { isPreCompressed } from "./entity/upload-file";
import { NodeHistogramReq, Histogram } from "./type/node";
import { UpdatePriorReq } from "./type/prior-def";
import { StoreInfo } from "./type/info";

import { TQueue } from "../../context/queue/types";
import { TError } from "../../types/error";

import { DiscretizeAdapter } from "./adapter/node/discretize";
import { ExtractAdapter } from "./adapter/prior/extract";
import { SettingAdapter } from "./adapter/prior/setting";
import { SummaryAdapter } from "./adapter/node/summary";
import { AssignAdapter } from "./adapter/prior/assign";
import { IdCodeAdapter } from "./adapter/idcode";
import { StoreAdapter } from "./adapter/store";
import { PriorAdapter } from "./adapter/prior";
import { GroupAdapter } from "./adapter/group";
import { NodeAdapter } from "./adapter/node";
import { SizeAdapter } from "./adapter/size";

import { IDataManagerRepo } from "./irepo";

export class DataManagerUsecase {
  private repo: IDataManagerRepo;
  private error: (error: TError) => void;

  constructor(repo: IDataManagerRepo, error: (error: TError) => void) {
    this.repo = repo;
    this.error = error;
  }

  async fetchInfo(): Promise<StoreInfo> {
    try {
      return await this.repo.fetchInfo();
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /**
   * 直近アップロードの進行状況を取得し、画面が使う形へ正規化して返す。
   *
   * ガード用途なので、失敗してもダイアログを出さない（this.error を呼ばない）。
   * 取得できないときは「未アップロード扱い」ではなく呼び出し側で握りつぶし、
   * ガードを出さない側に倒す。
   */
  async fetchSetupStatus(): Promise<SetupStatus> {
    const data = await this.repo.fetchCsvStorageStatus();
    return CsvStatusAdapter.toOutputDTO(data);
  }

  async fetchIdCode(): Promise<IdCode[]> {
    try {
      const data = await this.repo.fetchIdCode();
      return IdCodeAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async addIdCode(): Promise<any> {
    try {
      return await this.repo.addIdCode();
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async editIdCode(req: EditIdCodeReq): Promise<any> {
    try {
      const dto = IdCodeAdapter.toEditInputDTO(req);
      return await this.repo.editIdCode(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async deleteIdCode(req: DeleteIdCodeReq): Promise<any> {
    try {
      const dto = IdCodeAdapter.toDeleteInputDTO(req);
      return await this.repo.deleteIdCode(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchSummary(): Promise<any> {
    try {
      const data = await this.repo.fetchSummary();
      return SummaryAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchNode(language?: string): Promise<any[]> {
    try {
      const data = await this.repo.fetchNode();
      return NodeAdapter.toOutputDTO(data, language);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async editNode(req: any): Promise<any> {
    try {
      const dto = NodeAdapter.toInputDTO(req);
      return await this.repo.editNode(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchDiscretize(): Promise<any[]> {
    try {
      const data = await this.repo.fetchDiscretize();
      return DiscretizeAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async editDiscretize(req: any): Promise<any> {
    try {
      const dto = DiscretizeAdapter.toInputDTO(req);
      return await this.repo.editDiscretize(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchNodeHistogram(req: NodeHistogramReq): Promise<Histogram[]> {
    try {
      return await this.repo.fetchNodeHistogram(req);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchGroup(): Promise<Group[]> {
    try {
      const data = await this.repo.fetchGroup();
      return GroupAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async addGroup(req: AddGroupReq): Promise<any> {
    try {
      return await this.repo.addGroup(req);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async editGroup(req: EditGroupReq): Promise<any> {
    try {
      return await this.repo.editGroup(req);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async deleteGroup(req: DeleteGroupReq): Promise<any> {
    try {
      const dto = GroupAdapter.toInputDTO(req);
      return await this.repo.deleteGroup(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async checkFile(): Promise<TQueue[]> {
    try {
      const data = await this.repo.checkFile();
      return StoreAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async uploadFile(req: UploadFileReq): Promise<any> {
    try {
      return await this.repo.uploadFile(req);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async storeFile(req: StoreFileReq): Promise<any> {
    try {
      return await this.repo.storeFile(req);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  // ===== 2 フェーズ取込 (presigned → API-1 raw_ingest → API-2 derive) =====

  /**
   * アップロード開始 (API-1 まで): presigned 一括発行 → 各ファイルを gzip → S3 直 PUT → raw_ingest 起動。
   *
   * 複数ファイル (.csv / .csv.gz / .zip) を 1 job にまとめて取り込む (API-1 が結合する)。
   * .csv のみクライアント gzip の対象で、.csv.gz / .zip は既に確定拡張子なのでそのまま PUT する。
   * 戻り値 = ingest job の upload_id (以降のポーリング / derive で使う)。
   */
  async startUpload(req: StartUploadReq): Promise<string> {
    try {
      // 圧縮を先に行い、その結果 (gzip したか) を presigned 発行時に渡す。
      // サーバは compressed に応じて S3 キーの拡張子 (.csv.gz / .csv) を決める。
      const bodies = await Promise.all(
        req.files.map(async (file) => {
          if (isPreCompressed(file.name)) {
            return { file, blob: file as Blob, compressed: false };
          }
          const { blob, compressed } = await gzipBlob(file);
          return { file, blob, compressed };
        })
      );

      const presigned: PresignedUpload = await this.repo.issueUploadUrl({
        store_type: req.store_type,
        files: bodies.map((b) => ({
          file_name: b.file.name,
          size: b.file.size,
          compressed: b.compressed,
        })),
      });

      // レスポンスはリクエストと同順で返るが、取り違えを防ぐため file_name でも突き合わせる。
      await Promise.all(
        bodies.map((b, i) => {
          const target =
            presigned.files[i]?.file_name === b.file.name
              ? presigned.files[i]
              : presigned.files.find((f) => f.file_name === b.file.name);
          if (!target) {
            throw new Error(`presigned URL not issued for ${b.file.name}`);
          }
          return this.repo.putPresigned({ url: target.url, blob: b.blob });
        })
      );

      await this.repo.triggerRawIngest({ upload_id: presigned.upload_id });
      return presigned.upload_id;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /** 取込状態 (raw=API-1 / store=API-2 の進捗) を取得。 */
  async getIngestStatus(upload_id: string): Promise<IngestStatus> {
    try {
      return (await this.repo.getIngestStatus({ upload_id })) as IngestStatus;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /** ユーザ選択の層別化属性 (候補=整数/文字列・ちょうど 2) を検証保存。 */
  async confirmAttributes(
    upload_id: string,
    attribute_names: string[]
  ): Promise<IngestStatus> {
    try {
      return (await this.repo.confirmAttributes({
        upload_id,
        attribute_names,
      })) as IngestStatus;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /** ユーザ確定 finest (最小時間粒度) を保存。 */
  async confirmFinest(
    upload_id: string,
    confirmed_finest: string
  ): Promise<IngestStatus> {
    try {
      return (await this.repo.confirmFinest({
        upload_id,
        confirmed_finest,
      })) as IngestStatus;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /**
   * 取込を無効化する (旧 /store の「拒否」の 2 フェーズ版)。
   * アップロード済ファイルと未確定 raw parquet が削除され、job は REJECTED になる。
   */
  async cancelIngest(upload_id: string): Promise<IngestStatus> {
    try {
      return (await this.repo.cancelIngest({ upload_id })) as IngestStatus;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /**
   * 生成する時間粒度 (grain) セットを ingest job に保存する。finest はサーバが導出。
   * **ベンダー設定への適用はここでは起きない** (取込を承認したとき = API-2 起動時に適用される)。
   */
  async confirmGrains(upload_id: string, grains: string[]): Promise<IngestStatus> {
    try {
      return (await this.repo.confirmGrains({
        upload_id,
        grains,
      })) as IngestStatus;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /** ベンダーの選択可能/有効な時間粒度 (grain) を取得。 */
  async getTimeGrains(): Promise<TimeGrainConfig> {
    try {
      return (await this.repo.getTimeGrains()) as TimeGrainConfig;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /** ベンダーの時間粒度セットを全置換 (派生はこの grain セットを消費する)。 */
  async setTimeGrains(grains: string[]): Promise<TimeGrainConfig> {
    try {
      return (await this.repo.setTimeGrains({ grains })) as TimeGrainConfig;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /**
   * ベンダーの DWH スキーマを用意する (冪等)。ログイン直後とプロジェクト切替直後に呼ぶ。
   *
   * 失敗しても画面は使えるべきなので**握りつぶす** (エラー通知もしない)。未 provisioning の
   * ままなら後続の取込・設定 API がそれぞれのエラーを返す。
   */
  async provisionVendor(): Promise<boolean> {
    try {
      await this.repo.provisionVendor();
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * 設定変更タブ用: 現行データ (current 版) を作った取込 job の状態 + 列統計。
   * 取込フローと違い upload_id が手元に無いので、サーバが current 版から辿る。
   */
  async getCurrentIngest(): Promise<IngestStatus> {
    try {
      return (await this.repo.getCurrentIngest()) as IngestStatus;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /** 層別化属性を変更し、現行版の raw から作り直す (新しい版が作られる)。 */
  async changeAttributes(attribute_names: string[]): Promise<any> {
    try {
      return await this.repo.changeAttributes({ attribute_names });
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /** 現行ノードの集約方法 (node value → 方法)。保存が無いノードは backend が mean で返す。 */
  async getAggregations(): Promise<Record<string, string>> {
    try {
      const res = await this.repo.getAggregations();
      return res?.aggregations ?? {};
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /**
   * 現行版に生成グレインを追加する (削除・最小粒度の変更は不可)。
   * `aggregations` = ノード (value) → 集約方法 (未指定は平均値)。追加分と生成済みの粒度
   * (最小粒度以外) をこの方法で最小粒度から作り直す。
   */
  async addTimeGrains(
    grains: string[],
    aggregations: Record<string, string> = {}
  ): Promise<any> {
    try {
      return await this.repo.addTimeGrains({ grains, aggregations });
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /** API-2 (raw parquet → 時間粒度 parquet 生成 + DB 投入 + raw 確定) を起動。 */
  async startDerive(upload_id: string): Promise<any> {
    try {
      return await this.repo.triggerDerive({ upload_id });
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchGraphsMeta(): Promise<any> {
    try {
      const data = await this.repo.fetchGraphsMeta();
      return PriorAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async updateGraphStatus(req: UpdatePriorReq): Promise<any> {
    try {
      const dto = PriorAdapter.toInputDTO(req);
      return await this.repo.updateGraphStatus(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchSimilarTerms(req: any): Promise<any> {
    try {
      const dto = ExtractAdapter.toTermsInputDTO(req);
      const data = await this.repo.fetchSimilarTerms(dto);
      return ExtractAdapter.toTermsOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchExtractEdges(req: any): Promise<any> {
    try {
      const dto = ExtractAdapter.toExtractEdgesInputDTO(req);
      const data = await this.repo.fetchExtractEdges(dto);
      return ExtractAdapter.toExtractEdgesOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async saveExtractEdges(req: any): Promise<any> {
    try {
      const dto = ExtractAdapter.toSaveExtractEdgesInputDTO(req);
      return await this.repo.saveExtractEdges(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchExtractedEdges(): Promise<any> {
    try {
      const dto = ExtractAdapter.toExtractedEdgesInputDTO();
      const data = await this.repo.fetchExtractedEdges(dto);
      return ExtractAdapter.toExtractedEdgesOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async deleteExtractEdges(req: any): Promise<any> {
    try {
      const dto = ExtractAdapter.toCausalRuleInputDTO(req);
      return await this.repo.deleteExtractEdges(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchNodeAssign(): Promise<any> {
    try {
      const [edges, nodes] = await Promise.all([
        this.repo.fetchNodeAssign(),
        this.repo.fetchNodesAvailable(),
      ]);
      return AssignAdapter.toNodeAssignOutputDTO({ edges, nodes });
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchSimilarNodes(req: any): Promise<any> {
    try {
      const dto = AssignAdapter.toSimilarNodesInputDTO(req);
      const data = await this.repo.fetchSimilarNodes(dto);
      return AssignAdapter.toSimilarNodesOutputDTO(data, req);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async saveNodeAssign(req: any): Promise<any> {
    try {
      const dto = AssignAdapter.toNodeAssignInputDTO(req);
      return await this.repo.saveNodeAssign(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async updateGraphData(req: any): Promise<any> {
    try {
      const dto = SettingAdapter.toInputDTO(req);
      return await this.repo.updateGraphData(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchEdgeCandidates(req: any): Promise<any> {
    try {
      const data = await this.repo.fetchEdgeCandidates();
      return SettingAdapter.toOutputDTO(data, req);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async checkUploadFileSize(): Promise<number> {
    try {
      const data = await this.repo.checkFile();
      return SizeAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }
}
